import { useRouteScroll } from './useRouteScroll';
import { Suspense, useEffect } from 'react';
import { preloadPage } from './page-loaders';
import {
  BookOpen,
  Calculator,
  GraduationCap,
  Languages,
  House,
  Moon,
  Shirt,
  Sparkles,
  Sun,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { Link, NavLink, Outlet, useLocation, useNavigation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePreferences } from './PreferencesContext';
import { usePwa } from './PwaContext';
import { Button } from '../shared/ui/Button';
import { ActiveIndicator } from '../shared/ui/Motion';
import { StateDisplay } from '../shared/ui/StateDisplay';
import { Select } from '../shared/ui/Field';
import type { CloudBootstrapState } from '../cloud/bootstrap';

const navItems = [
  { to: '/', key: 'nav.home', icon: House },
  { to: '/calculate', key: 'nav.calculator', icon: Calculator },
  { to: '/stains', key: 'nav.stains', icon: Shirt },
  { to: '/guide', key: 'nav.guide', icon: BookOpen },
  { to: '/teacher', key: 'nav.teacher', icon: GraduationCap },
] as const;

function Navigation({ mobile = false }: { mobile?: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  return (
    <nav className={mobile ? 'bottom-nav' : 'side-nav'} aria-label={t('common.appName')}>
      <ActiveIndicator activeKey={pathname + t('common.language')} />
      {navItems
        .filter(({ to }) => !mobile || to !== '/')
        .map(({ to, key, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end
            viewTransition
            onPointerEnter={() => preloadPage(to)}
            onFocus={() => preloadPage(to)}
            className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}
          >
            <Icon aria-hidden="true" size={mobile ? 21 : 20} />
            <span>{t(key)}</span>
          </NavLink>
        ))}
    </nav>
  );
}

export function AppLayout({ cloudState }: { cloudState?: CloudBootstrapState }) {
  const { t } = useTranslation();
  const { locale, setLocale, theme, setTheme } = usePreferences();
  const pwa = usePwa();
  const location = useLocation();
  const navigation = useNavigation();
  useEffect(() => {
    const update = () =>
      document.documentElement.toggleAttribute('data-page-hidden', document.hidden);
    document.addEventListener('visibilitychange', update);
    update();
    return () => {
      document.removeEventListener('visibilitychange', update);
      document.documentElement.removeAttribute('data-page-hidden');
    };
  }, []);
  useRouteScroll();
  const isTeacherRoute = location.pathname === '/teacher';
  return (
    <div className="app-shell" data-navigating={navigation.state !== 'idle' || undefined}>
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById('main-content')?.focus();
        }}
      >
        {t('nav.skip')}
      </a>
      <aside className="desktop-sidebar no-print">
        <Link viewTransition to="/" className="brand" aria-label={t('nav.home')}>
          <img src="./app-icon.svg" alt="" width="46" height="46" />
          <div>
            <strong>{t('common.appName')}</strong>
            <small>{t('app.tagline')}</small>
          </div>
        </Link>
        <Navigation />
        <div className="sidebar-status">
          <span className={`network-status ${pwa.online ? 'online' : 'offline'}`}>
            {pwa.online ? (
              <Wifi size={17} aria-hidden="true" />
            ) : (
              <WifiOff size={17} aria-hidden="true" />
            )}
            {pwa.online ? t('app.online') : t('app.offline')}
          </span>
        </div>
      </aside>
      <div className="app-main">
        <header className="app-header no-print">
          <Link viewTransition to="/" className="mobile-brand" aria-label={t('nav.home')}>
            <img src="./app-icon.svg" alt="" width="38" height="38" />
            <strong>{t('common.appName')}</strong>
          </Link>
          <div className="header-context">
            <span>{t('home.space')}</span>
            <span>/</span>
            <strong>
              {t(navItems.find(({ to }) => to === location.pathname)?.key ?? 'nav.home')}
            </strong>
          </div>
          <div className="header-tools">
            <label className="compact-control">
              <Languages size={18} aria-hidden="true" />
              <span className="sr-only">{t('common.language')}</span>
              <Select
                value={locale}
                onChange={(event) => setLocale(event.target.value as 'de' | 'ar')}
              >
                <option value="de">DE</option>
                <option value="ar">AR</option>
              </Select>
            </label>
            <label className="compact-control">
              {theme === 'dark' ? (
                <Moon size={18} aria-hidden="true" />
              ) : (
                <Sun size={18} aria-hidden="true" />
              )}
              <span className="sr-only">{t('common.theme')}</span>
              <Select
                value={theme}
                onChange={(event) => setTheme(event.target.value as 'system' | 'light' | 'dark')}
              >
                <option value="system">{t('common.themeSystem')}</option>
                <option value="light">{t('common.themeLight')}</option>
                <option value="dark">{t('common.themeDark')}</option>
              </Select>
            </label>
            {pwa.installAvailable ? (
              <Button
                className="install-button"
                size="sm"
                variant="secondary"
                icon={<Sparkles size={16} />}
                aria-label={t('pwa.install')}
                onClick={() => void pwa.install()}
              >
                <span className="install-button__label">{t('pwa.install')}</span>
              </Button>
            ) : null}
          </div>
        </header>
        {!pwa.online && cloudState?.mode !== 'cloud' ? (
          <div className="offline-banner no-print" role="status">
            <WifiOff size={18} aria-hidden="true" />
            {t('app.offline')}
          </div>
        ) : null}
        {cloudState?.availability === 'cached' ? (
          <div className="offline-banner no-print" role="status">
            <WifiOff size={18} aria-hidden="true" />
            {t('app.cloudCached')}
          </div>
        ) : null}
        {cloudState?.availability === 'defaults' ? (
          <div className="offline-banner no-print" role="alert">
            <WifiOff size={18} aria-hidden="true" />
            {t('app.cloudDefaults')}
          </div>
        ) : null}
        {pwa.needRefresh ? (
          <div className="update-banner no-print" role="status">
            <span>{t('pwa.updateAvailable')}</span>
            <div className="cluster">
              <Button size="sm" onClick={() => void pwa.update()}>
                {t('pwa.updateNow')}
              </Button>
              <Button size="sm" variant="ghost" onClick={pwa.dismissUpdate}>
                {t('pwa.later')}
              </Button>
            </div>
          </div>
        ) : null}
        {pwa.updateError ? (
          <div className="offline-banner no-print" role="alert">
            {t('pwa.updateFailed')}
          </div>
        ) : null}
        <main id="main-content" tabIndex={-1}>
          <div className="route-stage" key={location.pathname}>
            <Suspense fallback={<StateDisplay />}>
              <Outlet />
            </Suspense>
          </div>
          <footer className="project-credit">
            <span>
              {t('credits.label')} · <strong>{t('credits.name')}</strong>
            </span>
            <details>
              <summary>{t('credits.about')}</summary>
              <p>{t('credits.text')}</p>
              <small>{t('credits.rights')}</small>
            </details>
          </footer>
        </main>
      </div>
      {isTeacherRoute ? null : <Navigation mobile />}
    </div>
  );
}
